import { gql } from '@apollo/client'
import { STATUS } from '@btime/constants'
import { useSessionPermission } from '@btime/react-session'
import { compose, isNil, map, pick, reject, values } from 'ramda'
import { useState } from 'react'
import { useCloseModal, useOpenModal } from '../../components/modals/ModalsContext'
import { serviceOrdersClient } from '../apollo/api'

export const useDuplicateChecker = () => {
  const [findDuplicates, state] = useFindDuplicateServiceOrders()
  const shouldValidateDuplicates = useSessionPermission('validateDuplicateServiceOrders')
  const closeModal = useCloseModal()
  const openModal = useOpenModal()

  const check = async inputs => {
    const isCreating = !inputs.some(item => item.id)
    if (!isCreating || !shouldValidateDuplicates) return [inputs, () => {}]
    const {
      safeInputs = inputs,
      duplicateInputs = [],
      duplicateIds = [],
      errors = []
    } = (await findDuplicates(inputs)) || {}

    if (errors?.length) return [[], () => {}]

    if (!safeInputs?.length && duplicateInputs?.length) {
      closeModal()
      openModal('serviceOrders/duplicates', { duplicateInputs, duplicateIds })
      return [[], () => {}]
    }

    return [
      safeInputs,
      () => duplicateInputs?.length && openModal('serviceOrders/duplicates', { duplicateInputs, duplicateIds })
    ]
  }

  return [check, state]
}

const QUERY = gql`
  query ServiceOrders($filter: ServiceOrderFilter) {
    serviceOrders(filter: $filter, limit: 999999999) {
      id
      checklist {
        id
        name
      }
      place {
        id
        name
      }
    }
  }
`

const findDuplicateMatch = (duplicates, input) => {
  return duplicates.find(item => item.checklist.id === input.checklistId && item.place.id === input.placeId)
}

const formatDuplicateInput = (duplicate, input) => {
  if (!duplicate) return undefined

  return {
    ...input,
    checklist: duplicate.checklist,
    place: duplicate.place
  }
}

export const useFindDuplicateServiceOrders = () => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState(undefined)

  const findDuplicates = inputs => {
    setLoading(true)
    if (!inputs?.length) return { safeInputs: [], duplicateInputs: [], duplicateIds: [], errors: [] }

    const placeIds = inputs.map(item => item.placeId)
    const checklistIds = inputs.map(item => item.checklistId)

    return serviceOrdersClient
      .query({
        query: QUERY,
        variables: {
          filter: {
            placeIds,
            checklistIds,
            statusNames: compose(values, pick(['OPEN', 'RECEIVED', 'ACCEPT', 'EXECUTION', 'PAUSE']))(STATUS)
          }
        }
      })
      .then(({ data, errors }) => {
        if (errors) {
          setErrors(errors)
          return { safeInputs: [], duplicateInputs: [], duplicateIds: [], errors }
        }

        const foundDuplicates = data.serviceOrders

        const duplicateIds = foundDuplicates.map(item => item.id)

        const duplicateInputs = reject(
          isNil,
          map(input => {
            const duplicate = findDuplicateMatch(foundDuplicates, input)
            return formatDuplicateInput(duplicate, input)
          }, inputs)
        )

        const safeInputs = inputs.filter(input => !findDuplicateMatch(foundDuplicates, input))

        return { safeInputs, duplicateInputs, duplicateIds, errors: [] }
      })
      .finally(() => setLoading(false))
  }

  return [findDuplicates, { loading, errors }]
}
